/**
 * CoreDirectorCodesPage — Reference · Core Director Codes.
 *
 * Canonical mirror of the Jack Henry Core Director code catalog
 * (IdVerifyCode, CustCode, TinCode, CallClass, AcctRelCode, …). Composite
 * primary key (domain, code) — both parts are immutable once a row exists,
 * so they are disabled in the edit form.
 *
 * Reads the live catalog via /v3/config/core-director-codes; writes go
 * through the typed bearer-only admin surface (first arg '' = no bypass key):
 *   client.retail.admin.reference.coreDirectorCodes.upsert('', body)
 *   client.retail.admin.reference.coreDirectorCodes.remove('', domain, code)  // soft delete
 *
 * Built to match the ProductsPage CRUD reference: reload() via config read,
 * a DataTable with trailing Edit/Delete ghost buttons, a "New" Card action,
 * a ModalShell form using FormField primitives, a save() handler, and a
 * window.confirm soft-delete. A Banner surfaces save failures inside the modal.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CoreDirectorCodeUpsertBody } from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { TextField, TextArea, SelectField, BoolField, NumberField } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';

/**
 * Wire shape of a Core Director code row as returned by
 * /v3/config/core-director-codes. The config method types its data as the
 * shared `CoreDirectorCode` entity; we describe the fields the page actually
 * renders/edits and cast at the read boundary so the page stays strict-clean
 * without depending on the shared entity's exact optional-ness.
 */
interface Row {
  domain: string;
  code: string;
  label?: string | null;
  description?: string | null;
  customerType?: string | null;
  notes?: string | null;
  externalRef?: string | null;
  sortOrder?: number | null;
  isActive?: boolean;
}

/**
 * JHA code domains — copied VERBATIM from the legacy ROP-teams editor
 * (apps/ROP-teams/src/modules/Admin/editors/coreDirectorCodesEditor.tsx
 * KNOWN_DOMAINS). Do not invent domain values; these are the canonical set.
 */
const KNOWN_DOMAINS: ReadonlyArray<{ value: string; label: string; hint: string }> = [
  { value: 'CallClass', label: 'CallClass', hint: 'Call-report classification (Field 76 → product call class).' },
  { value: 'CustCode', label: 'CustCode', hint: 'Customer master classification (CIF customer-type code).' },
  { value: 'TinCode', label: 'TinCode', hint: 'Tax-identification type (SSN, EIN, ITIN, foreign).' },
  { value: 'AcctRelCode', label: 'AcctRelCode', hint: 'Account-relationship roles (primary, joint, beneficiary, custodian).' },
  { value: 'IdVerifyCode', label: 'IdVerifyCode', hint: 'CIP identity-verification document codes.' },
  { value: 'InsiderCode', label: 'InsiderCode', hint: 'Reg O insider-tracking codes.' },
  { value: 'MailDirCode', label: 'MailDirCode', hint: 'Statement / mail-direction codes.' },
  { value: 'CitizenshipStatus', label: 'CitizenshipStatus', hint: 'CIP citizenship-status flags.' },
  { value: 'Gender', label: 'Gender', hint: 'Customer gender codes (M / F / X / Other).' },
  { value: 'OwnershipRole', label: 'OwnershipRole', hint: 'Beneficial-ownership and control-role codes.' },
];

const DOMAIN_OPTIONS = KNOWN_DOMAINS.map((d) => ({ value: d.value, label: d.label }));

const DEFAULT_DOMAIN = KNOWN_DOMAINS[0]!.value;

interface EditDraft {
  isNew: boolean;
  domain: string;
  code: string;
  label: string;
  description: string;
  customerType: string;
  notes: string;
  externalRef: string;
  sortOrder: number | null;
  isActive: boolean;
}

function toNum(v: number | string | null | undefined): number | null {
  if (v == null) return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

function rowToDraft(r: Row): EditDraft {
  return {
    isNew: false,
    domain: r.domain,
    code: r.code,
    label: r.label ?? '',
    description: r.description ?? '',
    customerType: r.customerType ?? '',
    notes: r.notes ?? '',
    externalRef: r.externalRef ?? '',
    sortOrder: toNum(r.sortOrder),
    isActive: r.isActive ?? true,
  };
}

function blankDraft(): EditDraft {
  return {
    isNew: true,
    domain: DEFAULT_DOMAIN,
    code: '',
    label: '',
    description: '',
    customerType: '',
    notes: '',
    externalRef: '',
    sortOrder: 100,
    isActive: true,
  };
}

function draftToBody(d: EditDraft): CoreDirectorCodeUpsertBody {
  const trim = (s: string): string => s.trim();
  const nullable = (s: string): string | null => {
    const t = s.trim();
    return t === '' ? null : t;
  };
  return {
    domain: trim(d.domain),
    code: trim(d.code),
    label: trim(d.label),
    description: nullable(d.description),
    customerType: nullable(d.customerType),
    notes: nullable(d.notes),
    externalRef: nullable(d.externalRef),
    sortOrder: d.sortOrder,
    isActive: d.isActive,
  };
}

function domainHint(domain: string): string | undefined {
  return KNOWN_DOMAINS.find((d) => d.value === domain)?.hint;
}

export function CoreDirectorCodesPage(): JSX.Element {
  const client = useApiClient();
  const [rows, setRows] = useState<ReadonlyArray<Row> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('');
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      const resp = await client.retail.config.coreDirectorCodes();
      if (resp.success && Array.isArray(resp.data)) {
        setRows(resp.data as unknown as ReadonlyArray<Row>);
      } else {
        setError(resp.error ?? 'Failed to load Core Director codes.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [client]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const filtered = useMemo(() => {
    if (!rows) return rows;
    const n = filter.trim().toLowerCase();
    if (!n) return rows;
    return rows.filter((r) =>
      r.domain.toLowerCase().includes(n) ||
      r.code.toLowerCase().includes(n) ||
      (r.label ?? '').toLowerCase().includes(n) ||
      (r.description ?? '').toLowerCase().includes(n) ||
      (r.customerType ?? '').toLowerCase().includes(n)
    );
  }, [rows, filter]);

  // Sort by domain (known-domain order first, then extras alpha), then
  // sortOrder, then code — mirrors the legacy editor's grouping intent while
  // keeping a single flat, filterable table.
  const sorted = useMemo(() => {
    if (!filtered) return filtered;
    const order = (domain: string): number => {
      const i = KNOWN_DOMAINS.findIndex((d) => d.value === domain);
      return i === -1 ? KNOWN_DOMAINS.length : i;
    };
    return [...filtered].sort(
      (a, b) =>
        order(a.domain) - order(b.domain) ||
        a.domain.localeCompare(b.domain) ||
        (toNum(a.sortOrder) ?? 9999) - (toNum(b.sortOrder) ?? 9999) ||
        a.code.localeCompare(b.code)
    );
  }, [filtered]);

  const remove = useCallback(
    async (domain: string, code: string): Promise<void> => {
      if (
        !window.confirm(
          `Deactivate Core Director code ${domain}/${code}? It will be soft-deleted (isActive=false) and officers won't see it as a choice.`
        )
      ) {
        return;
      }
      setError(null);
      try {
        const resp = await client.retail.admin.reference.coreDirectorCodes.remove('', domain, code);
        if (resp.success === false) {
          setError(resp.error ?? 'Delete failed.');
          return;
        }
        await reload();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [client, reload]
  );

  const columns: ReadonlyArray<DataTableColumn<Row>> = useMemo(
    () => [
      {
        header: 'Domain',
        cell: (r) => <Pill tone="muted">{r.domain}</Pill>,
        sortKey: (r) => r.domain,
      },
      {
        header: 'Code',
        cell: (r) => <code className="cell-mono--bold">{r.code}</code>,
        sortKey: (r) => r.code,
      },
      {
        header: 'Label',
        cell: (r) => r.label ?? <span className="cell-dim">—</span>,
      },
      {
        header: 'Description',
        cell: (r) =>
          r.description ? r.description : <span className="cell-dim">—</span>,
      },
      {
        header: 'Customer Type',
        cell: (r) =>
          r.customerType ? (
            <code className="cell-mono">{r.customerType}</code>
          ) : (
            <span className="cell-dim">—</span>
          ),
      },
      {
        header: 'Sort',
        cell: (r) =>
          r.sortOrder != null ? (
            <span className="cell-mono">{r.sortOrder}</span>
          ) : (
            <span className="cell-dim">—</span>
          ),
        sortKey: (r) => toNum(r.sortOrder),
      },
      {
        header: 'Status',
        cell: (r) =>
          r.isActive ?? true ? (
            <Pill tone="ok">active</Pill>
          ) : (
            <Pill tone="muted">inactive</Pill>
          ),
      },
      {
        header: '',
        cell: (r) => (
          <div className="cell-actions">
            <Button size="sm" variant="ghost" onClick={() => setDraft(rowToDraft(r))}>
              Edit
            </Button>
            <Button size="sm" variant="ghost" onClick={() => void remove(r.domain, r.code)}>
              Delete
            </Button>
          </div>
        ),
      },
    ],
    [remove]
  );

  const save = useCallback(
    async (d: EditDraft): Promise<void> => {
      setSaving(true);
      setSaveErr(null);
      try {
        const body = draftToBody(d);
        const resp = await client.retail.admin.reference.coreDirectorCodes.upsert('', body);
        if (resp.success === false) {
          setSaveErr(resp.error ?? 'Save failed.');
          return;
        }
        setDraft(null);
        await reload();
      } catch (e) {
        setSaveErr(e instanceof Error ? e.message : String(e));
      } finally {
        setSaving(false);
      }
    },
    [client, reload]
  );

  if (error) return <Banner tone="err">Could not load Core Director codes: {error}</Banner>;
  if (!rows) return <Spinner label="Loading Core Director codes…" />;

  return (
    <>
      <Card
        title={`Core Director Codes — ${sorted?.length ?? 0} of ${rows.length}`}
        actions={
          <div className="users-filter">
            <TextField
              label=""
              value={filter}
              onChange={setFilter}
              placeholder="Filter by domain, code, label, description, or customer type…"
            />
            <Button onClick={() => setDraft(blankDraft())}>New</Button>
          </div>
        }
      >
        <Banner tone="warn">
          Reference data mirrored from Jack Henry Core Director. Adding or renaming a code here
          does not change Core Director itself — coordinate any change with the core admin team
          first.
        </Banner>
        <DataTable<Row>
          columns={columns}
          rows={sorted ?? []}
          rowKey={(r) => `${r.domain}/${r.code}`}
          emptyState={<p className="empty">No Core Director codes match the current filter.</p>}
        />
      </Card>

      <ModalShell
        open={draft !== null}
        title={
          draft
            ? draft.isNew
              ? 'New Core Director code'
              : `Edit code — ${draft.domain}/${draft.code}`
            : ''
        }
        onClose={() => (saving ? undefined : setDraft(null))}
        width={620}
        actions={
          draft ? (
            <>
              <Button variant="ghost" onClick={() => setDraft(null)} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={() => void save(draft)} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </>
          ) : null
        }
      >
        {draft && (
          <>
            {saveErr && <Banner tone="err">{saveErr}</Banner>}
            <SelectField
              label="Domain"
              value={draft.domain}
              onChange={(v) => setDraft({ ...draft, domain: v })}
              options={DOMAIN_OPTIONS}
              disabled={!draft.isNew}
              required
              hint={
                draft.isNew
                  ? domainHint(draft.domain) ?? 'JHA code domain. Permanent — part of the primary key.'
                  : 'Read-only after create (primary key part).'
              }
            />
            <TextField
              label="Code"
              value={draft.code}
              onChange={(v) => setDraft({ ...draft, code: v })}
              disabled={!draft.isNew}
              monospace
              required
              maxLength={16}
              hint={draft.isNew ? 'JHA code value (primary key part).' : 'Read-only after create (primary key part).'}
            />
            <TextField
              label="Label"
              value={draft.label}
              onChange={(v) => setDraft({ ...draft, label: v })}
              required
              hint="Friendly name shown in dropdowns and reports."
            />
            <TextArea
              label="Description"
              value={draft.description}
              onChange={(v) => setDraft({ ...draft, description: v })}
              rows={3}
              hint="Longer explanation shown in tooltips and admin help."
            />
            <TextField
              label="Customer Type"
              value={draft.customerType}
              onChange={(v) => setDraft({ ...draft, customerType: v })}
              hint="If this code only applies to one customer type (Individual, LLC, Trust…), name it here."
            />
            <TextField
              label="External Reference"
              value={draft.externalRef}
              onChange={(v) => setDraft({ ...draft, externalRef: v })}
              monospace
              hint="Link to a JHA bulletin or vendor doc (e.g. JHA-2026-005)."
            />
            <TextArea
              label="Notes"
              value={draft.notes}
              onChange={(v) => setDraft({ ...draft, notes: v })}
              rows={2}
              hint="Internal notes for compliance / ops."
            />
            <NumberField
              label="Sort Order"
              value={draft.sortOrder}
              onChange={(v) => setDraft({ ...draft, sortOrder: v })}
              min={0}
              hint="Display order within the domain."
            />
            <BoolField
              label="Active"
              value={draft.isActive}
              onChange={(v) => setDraft({ ...draft, isActive: v })}
              hint="Inactive codes are hidden from officers on new opens."
            />
          </>
        )}
      </ModalShell>
    </>
  );
}
